/**
 * Client for zeecore /fleet-intelligence/config/fleet-cost-assumptions.
 * Persists via ObjectStore (local FS in dev, S3 in prod) — same as Costs / Utilisation.
 */

import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import type {
  FleetCostAssumptionsConfig,
  FleetCostAssumptionsSaveResult,
} from '../types/fleetCostAssumptions';

export async function fetchFleetCostAssumptionsConfig(): Promise<FleetCostAssumptionsConfig> {
  const response = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.config.fleetCostAssumptions(), {
    cache: 'no-store',
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Fleet cost assumptions load failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as FleetCostAssumptionsConfig;
}

/** Built-in server defaults (per vehicle class) — what "Reset" restores. */
export async function fetchFleetCostAssumptionsDefaults(): Promise<FleetCostAssumptionsConfig> {
  const response = await authenticatedFetch(
    API_ENDPOINTS.fleetIntelligence.config.fleetCostAssumptionsDefaults(),
    { cache: 'no-store' },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Fleet cost defaults load failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as FleetCostAssumptionsConfig;
}

export async function saveFleetCostAssumptionsConfig(
  config: FleetCostAssumptionsConfig,
  options?: { saveReason?: 'reset_to_defaults' },
): Promise<FleetCostAssumptionsSaveResult> {
  const response = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.config.fleetCostAssumptions(), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...config,
      ...(options?.saveReason ? { save_reason: options.saveReason } : {}),
    }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Fleet cost assumptions save failed: ${response.status} ${text.slice(0, 200)}`);
  }
  return (await response.json()) as FleetCostAssumptionsSaveResult;
}
